import { useMemo } from 'react'
import { HorizontalScroller } from '@/components/commons/horizontal-scroller'
import { StatCard } from '@/features/marquee/components/stat-card'
import { isCheckedIn, rsvpTally } from '@/features/marquee/utils'
import type {
	MarqueeAttendee,
	MarqueeEventCounts,
	MarqueeEventState
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface AttendeeSummaryProps {
	/**
	 * The whole roster. The list is never paginated, so the RSVP and attendance
	 * tallies are exact rather than a page's worth.
	 */
	rows: MarqueeAttendee[]
	counts?: MarqueeEventCounts
	/** Total invited attendees (event-level count). */
	invited?: number
	/**
	 * The event's lifecycle state, which is what says whether the invite answers
	 * can still move: the lock closes them (product rule, 2026-08-04). It only
	 * picks the caption's wording, never a count, and never relabels a row.
	 */
	state?: MarqueeEventState
	isLoading?: boolean
}

/** Shown when the figure is genuinely unknown, never as a stand-in for zero. */
const PLACEHOLDER = '-'

/**
 * Stat row above the attendees table: Invited / Declined / No response /
 * Attendance. It scrolls horizontally when the tiles overflow.
 *
 * Declined and No response are the invite answers the import carried
 * (`rsvp_status` per row); they read `-` while the roster carries no RSVP data at
 * all, since a zero there would be a claim the data cannot support.
 *
 * No response spans `invited` and `no_response` together: both mean the person
 * never answered. Which of the two the backend stores is a matter of when, not of
 * fact, so the number stays stable across the lock and the caption states whether
 * answers are still open. The lock rewrites `invited` to `no_response` in the same
 * transaction as the state change, so the tile and the caption flip together on
 * the refetch; nothing here relabels a row client-side.
 *
 * Attendance is the **broad** present count, the same one the check-in header and
 * the tracker card show: everyone whose check-in landed as arrived or logged
 * anyway, walk-ins and late arrivals included. It is deliberately not counted off
 * the `checked_in` funnel lane, which excludes both and would undercount.
 */
export function AttendeeSummary({
	rows,
	counts,
	invited,
	state,
	isLoading
}: AttendeeSummaryProps) {
	const { t } = useTranslation()
	const loading = isLoading || !counts

	const rsvp = useMemo(() => rsvpTally(rows), [rows])
	const attendance = useMemo(() => rows.filter(isCheckedIn).length, [rows])

	const invitedValue = loading
		? PLACEHOLDER
		: String(invited ?? counts?.imported ?? 0)
	// A tally is only shown once we know the roster carries invite answers.
	const rsvpValue = (value: number | undefined) =>
		loading || value === undefined ? PLACEHOLDER : String(value)

	const cards = [
		{
			value: invitedValue,
			label: t('marquee:summary_invited'),
			sublabel: PLACEHOLDER
		},
		{
			value: rsvpValue(rsvp?.declined),
			label: t('marquee:summary_declined'),
			sublabel: PLACEHOLDER
		},
		{
			value: rsvpValue(rsvp?.awaiting),
			label: t('marquee:summary_no_response'),
			sublabel: rsvp
				? t(
						state && state !== 'pre_work'
							? 'marquee:summary_no_response_closed'
							: 'marquee:summary_no_response_open'
					)
				: PLACEHOLDER
		},
		{
			value: loading ? PLACEHOLDER : String(attendance),
			label: t('marquee:summary_attendance'),
			sublabel: t('marquee:summary_attendance_caption')
		}
	]

	// Full-bleed: the scroller cancels the page's horizontal padding (`-mx`) and
	// the track re-adds it (`px`), so the row scrolls edge-to-edge without the
	// sides looking clipped while the tiles still line up with the page content.
	// Tiles fill the width equally on desktop and become fixed-width scroll items
	// below the breakpoint.
	return (
		<HorizontalScroller className='-mx-4 l:-mx-10'>
			<div className='flex l:w-full w-max gap-4 l:px-10 px-4'>
				{cards.map((card) => (
					<StatCard
						className='l:min-w-0 min-w-[240px] flex-1'
						key={card.label}
						label={card.label}
						sublabel={card.sublabel}
						value={card.value}
					/>
				))}
			</div>
		</HorizontalScroller>
	)
}
